import { CONSTANTS } from "./constants";
import { faceLetters, wordFromPath } from "./board";
import type { Path, ScoreBreakdown, TileIndex } from "./types";

/**
 * How much a tile is worth right now.
 *
 * Every tile starts at its letter count — 1 normally, 2 for the Qu face — and
 * gains `HEAT_STEP` for each prior VALID word that crossed it. Misses never add
 * heat, so `uses` is only ever incremented on a hit.
 *
 *     fresh tile        1
 *     used once         2
 *     used twice        3
 *     fresh Qu tile     2   (the digraph is two letters)
 */
export function tileValue(board: string, tile: TileIndex, uses: number): number {
  const letters = faceLetters(board[tile]).length;
  return letters + Math.min(uses, CONSTANTS.HEAT_CAP) * CONSTANTS.HEAT_STEP;
}

/** Heat level 0..HEAT_CAP — what the UI tints and badges on. */
export function heatLevel(uses: number): number {
  return Math.min(uses, CONSTANTS.HEAT_CAP);
}

/**
 * The scoring formula.
 *
 *     value(i) = letters(i) + min(uses(i), HEAT_CAP) × HEAT_STEP
 *     sum      = Σ value(i) over the path
 *     gold     = GOLD_MULT if the path crosses the gold tile else 1
 *     base     = sum² × gold
 *     points   = base × rung
 *
 * Heat lives INSIDE the square, which is the whole point of the model: a tile
 * you have already used does not hand you a flat bonus, it makes every other
 * letter in the word worth more. A cold 4-letter word is (1+1+1+1)² = 16; the
 * same four tiles used once each are (2+2+2+2)² = 64. Long words routed back
 * over your own tracks compound hard, and that is intended.
 *
 * A cold path scores exactly `length²`, because every fresh tile contributes its
 * letter count — so cold scoring is unchanged from the flat model and the Qu
 * digraph still counts as two letters without a special case.
 *
 * Order is load-bearing: the square happens BEFORE the gold multiplier, and the
 * ladder multiplies the already-doubled base.
 *
 * `uses` is the per-tile count as it stands BEFORE this word lands.
 */
export function scorePath(
  board: string,
  path: Path,
  uses: readonly number[],
  goldTile: TileIndex,
  rung: number,
): ScoreBreakdown {
  const length = wordFromPath(board, path).length;

  let sum = 0;
  for (const t of path) sum += tileValue(board, t, uses[t] ?? 0);

  const squared = sum * sum;
  const gold = path.includes(goldTile);
  const base = squared * (gold ? CONSTANTS.GOLD_MULT : 1);

  return { length, sum, squared, heatPoints: sum - length, gold, base, rung, points: base * rung };
}

/** Cold base — the same formula with no heat and rung 1, i.e. `length² × gold`. */
export function coldBase(board: string, path: Path, goldTile: TileIndex): number {
  const length = wordFromPath(board, path).length;
  return length * length * (path.includes(goldTile) ? CONSTANTS.GOLD_MULT : 1);
}

/** The ladder: climb on a hit (capped), reset to 1 on a miss (§1.3). */
export function nextRung(rung: number, valid: boolean): number {
  return valid ? Math.min(rung + 1, CONSTANTS.MULT_MAX) : 1;
}

/**
 * "Same shots, perfect order" (§1.7): take the player's valid shots with the
 * base scores they actually realised, re-sort ascending, and replay them on a
 * clean 1..n ladder so the biggest base lands on the highest rung.
 *
 * This is a reordering of realised shots, NOT a global optimum — the bases are
 * frozen as fired, so it does not model the different heat a different order
 * would have produced. Labelled accordingly in the UI.
 */
export function perfectOrderTotal(validBases: readonly number[]): number {
  const ascending = [...validBases].sort((a, b) => a - b);
  let total = 0;
  for (let i = 0; i < ascending.length; i += 1) {
    total += ascending[i] * Math.min(i + 1, CONSTANTS.MULT_MAX);
  }
  return total;
}
